/*
 * argile-stores-map.js — Vue carte des points de vente.
 *
 * CHARGEMENT PARESSEUX — Mapbox GL JS (bibliothèque + feuille de style) n'est
 * téléchargé qu'au PREMIER affichage de l'onglet Carte. Un visiteur qui reste en vue
 * Liste ne télécharge rien et ne contacte aucun tiers : la page garde exactement la
 * performance qu'elle avait avant cette fonctionnalité.
 *
 * SOURCE DE DONNÉES — La carte se reconstruit depuis le DOM (`[data-store-card]`), pas
 * depuis un JSON dupliqué en Liquid. Une seule vérité : ce que la liste filtre, la carte
 * le montre. Un point sans coordonnées n'a pas d'attribut `data-lat` : il reste dans la
 * liste et sort de la carte, sans branche particulière.
 *
 * COUPLAGE — Ce fichier ne connaît pas `argile-stores.js` et réciproquement. Ils
 * communiquent par deux événements : `argile:stores:view` (l'onglet a changé) et
 * `argile:stores:render` (le jeu de résultats a changé).
 */
(() => {
  // Version épinglée : une URL de CDN erronée rend 404 et laisse un cadre gris muet.
  const VERSION = 'v3.26.0';
  const CDN = `https://api.mapbox.com/mapbox-gl-js/${VERSION}`;

  /** Promesse partagée : la bibliothèque n'est chargée qu'une fois pour toute la page. */
  let chargement = null;

  const chargerMapbox = () => {
    if (chargement) return chargement;
    chargement = new Promise((resolve, reject) => {
      const feuille = document.createElement('link');
      feuille.rel = 'stylesheet';
      feuille.href = `${CDN}/mapbox-gl.css`;
      document.head.appendChild(feuille);

      const script = document.createElement('script');
      script.src = `${CDN}/mapbox-gl.js`;
      script.onload = () =>
        window.mapboxgl
          ? resolve(window.mapboxgl)
          : reject(new Error('mapbox-gl chargé mais absent de window'));
      script.onerror = () => reject(new Error('mapbox-gl.js injoignable'));
      document.head.appendChild(script);
    });
    return chargement;
  };

  /**
   * Jeton de couleur du thème, NORMALISÉ pour Mapbox.
   *
   * Le design system rend `--color-foreground` en syntaxe CSS moderne —
   * `rgb(40 20 12 / 1.0)`, composantes séparées par des espaces, alpha après une barre
   * oblique. Le validateur de style Mapbox ne connaît que CSS Color 3 et REJETTE cette
   * forme. Et il ne lève pas : il émet un événement `error` et n'ajoute simplement pas
   * la couche. `--argile-clay` étant un hexadécimal, les amas passaient et les pastilles
   * isolées disparaissaient — une panne à moitié invisible.
   *
   * On repasse donc la valeur par le navigateur, seul juge fiable de ce qu'est une
   * couleur CSS, et il la renormalise en `rgb(40, 20, 12)`.
   */
  const couleurDS = (el, nom, repli) => {
    const brut = getComputedStyle(el).getPropertyValue(nom).trim();
    if (!brut) return repli;
    const sonde = document.createElement('span');
    sonde.style.color = brut;
    if (!sonde.style.color) return repli; // syntaxe incomprise du navigateur lui-même
    sonde.style.display = 'none';
    document.body.appendChild(sonde);
    const normalisee = getComputedStyle(sonde).color;
    sonde.remove();
    return normalisee || repli;
  };

  /**
   * Points de vente à afficher : ceux qui correspondent aux filtres ET qui sont
   * géocodés. On lit `data-match` plutôt que `is-hidden` : cette dernière classe sert
   * AUSSI à masquer les résultats au-delà de la limite du « Voir la suite », qui
   * correspondent pourtant bien à la recherche et doivent apparaître sur la carte.
   */
  const collecte = (root) =>
    Array.from(root.querySelectorAll('[data-store-card][data-lat][data-lng]')).filter(
      (card) => card.getAttribute('data-match') !== '0'
    );

  const versGeoJSON = (cartes) => ({
    type: 'FeatureCollection',
    features: cartes.map((card, i) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [Number(card.dataset.lng), Number(card.dataset.lat)],
      },
      // On ne met QUE la clé d'appariement : tout le détail vit dans le DOM de la
      // colonne, jamais recopié ici. Une clé stable, pas un index de position — le tri
      // réordonne le DOM.
      properties: { id: card.dataset.storeId || String(i) },
    })),
  });

  /**
   * Largeur, en pixels, que le panneau de résultats RECOUVRE sur la droite de la carte.
   *
   * Depuis le 10/09 le panneau flotte au-dessus de la carte : la zone réellement
   * visible n'est plus le cadre de la carte. Sans cette mesure, un recadrage centre
   * les points SOUS le panneau — ils existent, et personne ne les voit.
   *
   * Mesuré à chaque appel plutôt que codé en dur : la largeur du panneau dépend de la
   * fenêtre (`min(38rem, 45vw)`), et sous 990 px il ne flotte plus du tout — il
   * redevient une rangée sous la carte, et le recouvrement vaut alors 0 sans qu'aucun
   * code n'ait à connaître le point de rupture.
   */
  const recouvrementDroite = (root) => {
    const pane = root.querySelector('.argile-stores__pane');
    const carte = root.querySelector('[data-stores-map]');
    if (!pane || !carte) return 0;
    const rp = pane.getBoundingClientRect();
    const rc = carte.getBoundingClientRect();
    if (!rp.width || !rc.width) return 0;
    // Chevauchement sur LES DEUX axes : empilé sous la carte, le panneau déborde encore
    // horizontalement, et ne recouvre pourtant rien.
    const chevauche = rp.top < rc.bottom && rp.bottom > rc.top && rp.right > rc.left;
    return chevauche ? Math.max(0, Math.round(rc.right - rp.left)) : 0;
  };

  const FRANCE = [[-5.2, 41.3], [9.6, 51.1]];

  /**
   * Cadrage d'ouverture.
   *
   * La France par défaut : sur la boutique française, les 120 points vont de New York à
   * Shanghai et les embrasser tous donne une mappemonde inutile.
   *
   * 🔴 MAIS PAS SUR UNE BOUTIQUE DONT LES POINTS SONT AILLEURS. Constaté le 15/09 en
   * déployant sur la boutique UK : ses deux seules adresses sont à Londres et dans le
   * Surrey, et la page s'ouvrait sur la France, Royaume-Uni hors champ — la pastille
   * était collée au coin de la carte.
   *
   * La règle ne nomme AUCUNE boutique : on cadre sur les points quand ils tiennent
   * ensemble, on garde la France quand ils sont dispersés au point que les embrasser ne
   * montrerait plus rien. 15° de longitude ≈ la largeur de la France plus ses voisins
   * immédiats ; les 195° du catalogue français passent donc très au-dessus du seuil et
   * son cadrage ne bouge pas.
   */
  const cadrageInitial = (root) => {
    const pts = collecte(root)
      .map((c) => [Number(c.dataset.lng), Number(c.dataset.lat)])
      .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
    if (!pts.length) return FRANCE;
    const lngs = pts.map((p) => p[0]);
    const lats = pts.map((p) => p[1]);
    const bornes = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
    const largeur = bornes[1][0] - bornes[0][0];
    const hauteur = bornes[1][1] - bornes[0][1];
    return largeur > 15 || hauteur > 12 ? FRANCE : bornes;
  };

  /** Marges de recadrage, panneau compris. */
  const margesCadrage = (root, base) => ({
    top: base,
    bottom: base,
    left: base,
    right: base + recouvrementDroite(root),
  });

  /** Décalage de caméra pour qu'un point recentré ne finisse pas sous le panneau. */
  const decalageCamera = (root) => [-recouvrementDroite(root) / 2, 0];

  const init = (root) => {
    const conteneur = root.querySelector('[data-stores-map]');
    if (!conteneur || conteneur.dataset.pret === '1') return;

    const erreur = root.querySelector('[data-stores-map-error]');
    const montrerErreur = () => {
      if (erreur) erreur.hidden = false;
      conteneur.hidden = true;
    };

    // Le jeton est porté par la racine de la section : la recherche géographique s'en
    // sert aussi, et une seconde copie finirait par diverger.
    const jeton = root.dataset.mapboxToken;
    // Sans jeton, inutile de charger 900 ko pour afficher un cadre gris.
    if (!jeton) return montrerErreur();

    conteneur.dataset.pret = '1';

    chargerMapbox()
      .then((mapboxgl) => {
        // TÉLÉMÉTRIE — Il n'existe AUCUN moyen supporté de la couper en GL JS v3.
        // `mapboxgl.config.EVENTS_URL` est le seul champ en accesseur LECTURE SEULE de
        // toute la config (les 23 autres sont inscriptibles) : Mapbox l'a délibérément
        // verrouillé. Et comme ce fichier est chargé en `type="module"`, donc en mode
        // strict, une affectation dessus ne rate pas en silence — elle LÈVE, et la
        // carte entière ne se construit jamais. Ne pas retenter.
        // La vraie protection est ailleurs : rien n'est envoyé à Mapbox tant que le
        // visiteur n'a pas ouvert l'onglet Carte.
        mapboxgl.accessToken = jeton;

        const encre = couleurDS(root, '--color-foreground', '#2B2B2B');
        const argile = couleurDS(root, '--argile-clay', '#86452B');

        const map = new mapboxgl.Map({
          container: conteneur,
          // STANDARD, ET RIEN D'AUTRE. Un réglage d'éditeur permettait de coller
          // l'URL d'un style maison : retiré, il ne pouvait rien apporter. Tout le
          // rendu vient d'ici (projection, thème monochrome, POI et 3D coupés) via
          // `setConfigProperty`, qui n'existe QUE sur Standard — sur `light-v11`, le
          // repli d'alors, la configuration ci-dessous était ignorée en silence.
          style: 'mapbox://styles/mapbox/standard',
          // Mercator et non le globe : GL JS v3 projette en globe par défaut, ce qui
          // affiche la Terre sur fond étoilé au dézoom. Spectaculaire, et déroutant sur
          // une page « où acheter » — le visiteur cherche une boutique, pas une vue
          // orbitale. Ce n'est PAS une propriété de configuration du style : ça se
          // décide ici, à la construction.
          projection: 'mercator',
          // Cadrage FRANCE au chargement, pas sur l'ensemble des points : ces derniers
          // vont de New York à Shanghai, et les embrasser tous donne une mappemonde
          // inutile. Le recadrage sur les résultats n'a lieu qu'après une recherche.
          bounds: cadrageInitial(root),
          // `maxZoom` : sans lui, une boutique à UN SEUL point ouvrirait au niveau de la
          // rue, sans aucun repère autour.
          // 56 et non 24, comme le recadrage d'après recherche : à 24, mesuré sur la
          // boutique UK, la pastille de Londres atterrissait à 24 px du bord haut —
          // visible, mais collée à l'arête.
          fitBoundsOptions: { padding: margesCadrage(root, 56), maxZoom: 11 },
          // LA MOLETTE NE ZOOME PLUS — demande de Camille du 04/09 : « qu'on ne puisse
          // pas zoomer/dézoomer au scroll quand on est au-dessus de la carte, de toute
          // façon on peut le faire avec le + / - ».
          //
          // ⚠️ TROISIÈME POSITION SUR CE RÉGLAGE. Ne pas le rouvrir sans lire ceci :
          //   1. `cooperativeGestures: true` au départ — molette = page, Ctrl+molette =
          //      zoom. Retiré le 03/09, jugé non conforme à la référence.
          //   2. Rien — la molette zoomait directement. C'est ce que Camille a vu, et
          //      c'est précisément ce qu'elle ne veut pas.
          //   3. `scrollZoom: false` — la molette ne fait plus rien SUR la carte, donc la
          //      page défile normalement par-dessus. C'est l'état actuel.
          // On préfère ça aux gestes coopératifs : ceux-ci gardent le zoom au
          // Ctrl+molette et affichent un bandeau d'explication, deux choses que personne
          // n'a demandées. Le zoom reste accessible par les boutons + / − de la
          // `NavigationControl`, ajoutée juste après.
          //
          // ⚠️ CE RÉGLAGE NE COUVRE QUE LA MOLETTE. Au doigt, `dragPan` déplace toujours
          // la carte : sur mobile, le défilement vertical de la page ne passe donc
          // toujours pas au-dessus d'elle. Signalé au client, non tranché à ce jour.
          scrollZoom: false,
          // Attribution reprise à la main plus bas : par défaut elle se pose en bas à
          // DROITE, c'est-à-dire sous le panneau de résultats depuis le 10/09. Une
          // attribution recouverte n'est pas une question de goût — Mapbox et
          // OpenStreetMap l'exigent visible.
          attributionControl: false,
        });
        // 🔴 EN BAS À GAUCHE, ET PAS EN HAUT À DROITE. Le panneau de résultats occupe
        // désormais le coin haut-droit : les boutons + / − y passaient dessous. Or ils
        // sont le SEUL accès au zoom depuis que la molette est neutralisée (décision de
        // Camille du 04/09) — les perdre, c'est perdre le zoom. C'est aussi le coin où
        // la maquette les place.
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-left');
        map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

        /** Allume une pastille, ou aucune quand `id` est nul. */
        const surligner = (id) => {
          if (!map.getLayer('pdv-actif')) return;
          map.setFilter('pdv-actif', ['==', ['get', 'id'], id == null ? '\u0000' : String(id)]);
        };

        /**
         * Allège le fond pour que les pastilles ressortent. Ces clés n'existent que sur
         * le style Standard, sur lequel la carte est désormais figée ; le garde-fou
         * reste pour le jour où Mapbox en renommera une — perdre le fond monochrome
         * vaut mieux que perdre la carte.
         */
        const allegerLeFond = () => {
          const reglages = {
            theme: 'monochrome', // le fond recule, l'argile des pastilles devient la seule couleur
            showPointOfInterestLabels: false, // restaurants, hôtels, commerces : du bruit ici
            showTransitLabels: false,
            show3dObjects: false, // bâtiments et relief agitent la carte sans rien apprendre
          };
          for (const [cle, valeur] of Object.entries(reglages)) {
            try {
              map.setConfigProperty('basemap', cle, valeur);
            } catch (e) {
              console.warn('[argile-stores-map] réglage de fond ignoré :', cle, e.message);
            }
          }
        };

        /** @param {boolean} recadrer — vrai seulement après une recherche géographique. */
        const rafraichir = (recadrer) => {
          const cartes = collecte(root);
          const fc = versGeoJSON(cartes);
          const source = map.getSource('pdv');
          if (source) source.setData(fc);
          conteneur._parId = new Map(cartes.map((c) => [c.dataset.storeId, c]));

          if (!recadrer || !fc.features.length) return;
          const bornes = new mapboxgl.LngLatBounds();
          fc.features.forEach((f) => bornes.extend(f.geometry.coordinates));
          map.fitBounds(bornes, { padding: margesCadrage(root, 56), maxZoom: 13, duration: 300 });
        };

        // `style.load` et NON `load` : `load` attend qu'une passe de rendu complète
        // ait eu lieu. Or `requestAnimationFrame` est gelé dans un onglet en
        // arrière-plan — un visiteur qui ouvre l'onglet Carte puis bascule sur un
        // autre onglet du navigateur n'aurait jamais ses pastilles. `style.load` se
        // déclenche dès que le style est prêt, indépendamment du rendu.
        map.on('style.load', () => {
          map.addSource('pdv', {
            type: 'geojson',
            data: versGeoJSON(collecte(root)),
            cluster: true,
            // 99 points français très denses en Île-de-France, 21 dispersés sur
            // 13 pays : le rayon doit regrouper au zoom pays sans coller au zoom ville.
            clusterRadius: 45,
            clusterMaxZoom: 12,
          });

          map.addLayer({
            id: 'pdv-clusters',
            type: 'circle',
            source: 'pdv',
            filter: ['has', 'point_count'],
            paint: {
              'circle-color': argile,
              'circle-opacity': 0.85,
              'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 30, 28],
            },
          });

          map.addLayer({
            id: 'pdv-clusters-count',
            type: 'symbol',
            source: 'pdv',
            filter: ['has', 'point_count'],
            layout: {
              'text-field': ['get', 'point_count_abbreviated'],
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              'text-size': 12,
            },
            paint: { 'text-color': '#FFFFFF' },
          });

          map.addLayer({
            id: 'pdv-point',
            type: 'circle',
            source: 'pdv',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-color': encre,
              'circle-radius': 7,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#FFFFFF',
            },
          });

          // Couche de surlignage : une seule pastille à la fois, désignée par filtre.
          // Un filtre plutôt qu'un `feature-state` : sur une source groupée, l'état par
          // entité ne survit pas au regroupement.
          map.addLayer({
            id: 'pdv-actif',
            type: 'circle',
            source: 'pdv',
            filter: ['==', ['get', 'id'], '\u0000'],
            paint: {
              'circle-color': argile,
              'circle-radius': 11,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#FFFFFF',
            },
          });

          // Un clic sur un amas zoome sur son contenu.
          map.on('click', 'pdv-clusters', (e) => {
            const [amas] = map.queryRenderedFeatures(e.point, { layers: ['pdv-clusters'] });
            map.getSource('pdv').getClusterExpansionZoom(amas.properties.cluster_id, (err, zoom) => {
              if (err) return;
              map.easeTo({
                center: amas.geometry.coordinates,
                zoom,
                offset: decalageCamera(root),
              });
            });
          });

          // PLUS D'INFOBULLE (retour Camille du 03/09). Elle était le seul accès au
          // détail tant que la colonne n'affichait qu'un nom et une localité ; celle-ci
          // porte maintenant la carte complète, l'infobulle ferait doublon et masquerait
          // la carte. Le clic sur une pastille se contente donc d'allumer le point et de
          // faire défiler la colonne jusqu'à sa fiche — la synchronisation existait déjà.
          map.on('click', 'pdv-point', (e) => {
            const point = e.features[0];
            const id = String(point.properties.id);
            const card = conteneur._parId && conteneur._parId.get(id);
            if (!card) return;
            surligner(id);
            // La colonne suit le clic sur la pastille : sans ça, la carte est un
            // cul-de-sac — on voit où c'est, sans pouvoir revenir au détail.
            root.dispatchEvent(
              new CustomEvent('argile:stores:pin', { bubbles: true, detail: { id, root } })
            );
          });

          for (const couche of ['pdv-clusters', 'pdv-point']) {
            map.on('mouseenter', couche, () => (map.getCanvas().style.cursor = 'pointer'));
            map.on('mouseleave', couche, () => (map.getCanvas().style.cursor = ''));
          }

          allegerLeFond();
          rafraichir(false);

          // Survol d'une ligne de la colonne → la pastille s'allume.
          document.addEventListener('argile:stores:highlight', (e) => {
            if (!e.detail || e.detail.root !== root) return;
            surligner(e.detail.id);
          });

          // Clic sur une fiche de la colonne → la pastille s'allume.
          document.addEventListener('argile:stores:focus', (e) => {
            if (!e.detail || e.detail.root !== root) return;
            const id = String(e.detail.id);
            const src = map.getSource('pdv');
            const cible = ((src && src._data.features) || []).find(
              (f) => String(f.properties.id) === id
            );
            if (!cible) return; // point non géocodé : rien à montrer sur la carte
            surligner(id);
            // CARTE FIGÉE (retour client du 03/09 : « elle bouge tout le temps »). Un clic
            // dans la colonne recentrait et forçait le zoom à 12 : parcourir la liste
            // faisait sauter la carte à chaque ligne. On ne bouge plus la caméra… sauf si
            // le point est HORS CHAMP, sinon la pastille s'allumerait là où personne ne
            // la voit et le clic aurait l'air sans effet. Dans ce cas on translate
            // seulement : le niveau de zoom du visiteur n'est jamais modifié.
            // ⚠️ `getBounds().contains()` NE SUFFIT PLUS depuis que le panneau flotte :
            // un point peut être dans le cadre de la carte et caché sous le panneau. On
            // projette donc en pixels et on retire la bande recouverte — sinon un clic
            // sur une fiche allume une pastille que personne ne voit, et paraît sans
            // effet.
            const recouvert = recouvrementDroite(root);
            const p = map.project(cible.geometry.coordinates);
            const marge = 24;
            const visible =
              p.x > marge &&
              p.x < conteneur.clientWidth - recouvert - marge &&
              p.y > marge &&
              p.y < conteneur.clientHeight - marge;
            if (!visible) {
              map.easeTo({
                center: cible.geometry.coordinates,
                offset: decalageCamera(root),
                duration: 300,
              });
            }
          });
        });

        map.on('error', (e) => {
          // TOUJOURS TRACER D'ABORD. Enregistrer un écouteur `error` supprime le report
          // console par défaut de Mapbox : un handler qui filtre sans tracer rend
          // muettes TOUTES les autres erreurs — validation de couche, tuile refusée,
          // source invalide. C'est ce qui a masqué le rejet d'une couche le 01/09.
          console.error('[argile-stores-map]', e.error || e);
          // Un jeton refusé (domaine non autorisé) mérite en plus un message visible.
          if (e?.error?.status === 401 || e?.error?.status === 403) montrerErreur();
        });

        conteneur._map = map;
        document.addEventListener('argile:stores:render', (e) => {
          // ⚠️ NE PAS conditionner à `map.isStyleLoaded()` : c'est un état dérivé de la
          // BOUCLE DE RENDU, faux tant qu'aucune image n'a été peinte — et gelé dans un
          // onglet d'arrière-plan. Le rafraîchissement était alors purement sauté : la
          // liste annonçait 3 résultats et la carte en montrait 120. `rafraichir` vérifie
          // déjà l'existence de la source, ce garde-fou était redondant autant que nuisible.
          //
          // On ne RECADRE, en revanche, que si une recherche géographique est active :
          // sinon le moindre filtre ferait sauter la carte d'un bout de l'Europe à l'autre.
          if (e.detail?.root === root) rafraichir(Boolean(e.detail.origine));
        });
      })
      .catch((cause) => {
        // NE JAMAIS AVALER LA CAUSE. Ce `.catch()` couvre tout le bloc ci-dessus :
        // sans trace, n'importe quelle ligne qui lève devient « la carte ne s'affiche
        // pas », sans le moindre indice. C'est ce qui est arrivé le 01/09 — une
        // affectation illégale en mode strict, trente secondes de correctif, une
        // demi-heure d'enquête parce que l'erreur n'allait nulle part.
        console.error('[argile-stores-map] carte non initialisée :', cause);
        montrerErreur();
      });
  };

  // La Carte étant la vue par défaut, aucun clic ne déclenchera l'initialisation : on
  // regarde donc au démarrage si le panneau est déjà affiché. On ne peut pas compter sur
  // l'événement de bascule — `argile-stores.js` s'exécute AVANT ce fichier, son
  // éventuelle diffusion initiale partirait donc alors que personne n'écoute encore.
  const amorcer = () =>
    document.querySelectorAll('.argile-stores').forEach((root) => {
      const panneau = root.querySelector('[data-stores-panel="carte"]');
      if (panneau && !panneau.hidden) init(root);
    });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', amorcer);
  } else {
    amorcer();
  }

  document.addEventListener('argile:stores:view', (e) => {
    const { view, root } = e.detail || {};
    if (view !== 'carte' || !root) return;
    init(root);
    // Le conteneur était masqué à l'initialisation d'une session précédente : sans
    // resize, Mapbox garde les dimensions qu'il avait au moment du calcul.
    const conteneur = root.querySelector('[data-stores-map]');
    if (conteneur?._map) requestAnimationFrame(() => conteneur._map.resize());
  });
})();
